import React, { useState, useEffect, useRef } from "react";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import ExplorePostCard from "./ExplorePostCard";
import styles from "./Explore.module.scss";

const TAGS = ["Travel", "Funny", "Photography", "News", "Pets", "RoadTrip"];

const ExploreYourWorld = ({
	posts = [],
	isLoading = false,
	selectedTag,
	setSelectedTag,
}) => {
	const [isMobile, setIsMobile] = useState(window.innerWidth < 431);
	const sliderRef = useRef(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	// Mouse drag scrolling state
	const [isDragging, setIsDragging] = useState(false);
	const [dragStartX, setDragStartX] = useState(0);
	const [dragScrollLeft, setDragScrollLeft] = useState(0);
	const [hasDraggedFar, setHasDraggedFar] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 431);
			checkScroll();
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const checkScroll = () => {
		if (!sliderRef.current) return;
		const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
		setCanScrollLeft(scrollLeft > 8);
		setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 8);
	};

	useEffect(() => {
		// Check scroll after posts update
		const timer = setTimeout(() => {
			checkScroll();
		}, 100);
		return () => clearTimeout(timer);
	}, [posts]);

	const scrollSlider = (direction) => {
		if (!sliderRef.current) return;
		const containerWidth = sliderRef.current.clientWidth;
		const scrollAmount = Math.max(containerWidth * 0.75, 260);
		sliderRef.current.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});
	};

	const handleMouseDown = (e) => {
		if (!sliderRef.current) return;
		setIsDragging(true);
		setHasDraggedFar(false);
		setDragStartX(e.pageX - sliderRef.current.offsetLeft);
		setDragScrollLeft(sliderRef.current.scrollLeft);
	};

	const handleMouseMove = (e) => {
		if (!isDragging || !sliderRef.current) return;
		e.preventDefault();
		const x = e.pageX - sliderRef.current.offsetLeft;
		const walk = (x - dragStartX) * 1.2;
		if (Math.abs(walk) > 6) {
			setHasDraggedFar(true);
		}
		sliderRef.current.scrollLeft = dragScrollLeft - walk;
	};

	const handleMouseUpOrLeave = () => {
		setIsDragging(false);
	};

	const handleTagClick = (tag) => {
		if (setSelectedTag) {
			setSelectedTag((prev) => (prev === tag ? null : tag));
		}
	};

	return (
		<>
			{/* Mobile Tag Bar */}
			{isMobile && (
				<div className={styles.TagContainer}>
					<div className={styles.btnContainer}>
						{TAGS.map((tag) => {
							const isActive = selectedTag === tag;
							return (
								<button
									key={tag}
									type="button"
									style={{
										background: isActive ? "#3A0DBB" : "",
										color: isActive ? "#fff" : "",
									}}
									onClick={() => handleTagClick(tag)}
								>
									{tag}
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Recommended Slider Section */}
			<div className={styles.recommendedContainer}>
				<div className={styles.sliderHeaderRow}>
					<p>Recommend For you</p>
					{posts.length > 0 && (
						<div className={styles.sliderHeaderArrows}>
							<button
								type="button"
								className={`${styles.headerArrowBtn} ${!canScrollLeft ? styles.arrowDisabled : ""}`}
								onClick={() => scrollSlider("left")}
								disabled={!canScrollLeft}
								aria-label="Previous recommendation"
							>
								<FaAngleLeft />
							</button>
							<button
								type="button"
								className={`${styles.headerArrowBtn} ${!canScrollRight ? styles.arrowDisabled : ""}`}
								onClick={() => scrollSlider("right")}
								disabled={!canScrollRight}
								aria-label="Next recommendation"
							>
								<FaAngleRight />
							</button>
						</div>
					)}
				</div>

				<div className={styles.sliderWrapper}>
					{/* Side floating arrows for desktop */}
					{canScrollLeft && (
						<button
							type="button"
							className={`${styles.sideArrowBtn} ${styles.sidePrev}`}
							onClick={() => scrollSlider("left")}
							aria-label="Previous recommendation"
						>
							<FaAngleLeft />
						</button>
					)}

					<div
						ref={sliderRef}
						className={`${styles.sliderTrack} ${isDragging ? styles.dragging : ""}`}
						onMouseDown={handleMouseDown}
						onMouseMove={handleMouseMove}
						onMouseUp={handleMouseUpOrLeave}
						onMouseLeave={handleMouseUpOrLeave}
						onScroll={checkScroll}
					>
						{isLoading && posts.length === 0 ? (
							Array.from({ length: 4 }).map((_, idx) => (
								<div key={idx} className={styles.sliderSkeletonCard} />
							))
						) : posts.length === 0 ? (
							<div className={styles.emptyFeedMessage}>
								<p>No posts available to display right now.</p>
							</div>
						) : (
							posts.map((post, index) => (
								<ExplorePostCard
									key={post._id || index}
									post={post}
									variant="slider"
									isDragging={hasDraggedFar}
								/>
							))
						)}
					</div>

					{canScrollRight && (
						<button
							type="button"
							className={`${styles.sideArrowBtn} ${styles.sideNext}`}
							onClick={() => scrollSlider("right")}
							aria-label="Next recommendation"
						>
							<FaAngleRight />
						</button>
					)}
				</div>
			</div>

			{/* Explore Main Grid Feed */}
			<div className={styles.ExploreImageMainContainer}>
				{isMobile ? null : <h3>Explore</h3>}
				{isLoading && posts.length === 0 ? (
					<div className={styles.ExploreImageContainer}>
						{Array.from({ length: 9 }).map((_, idx) => (
							<div key={idx} className={styles.gridSkeletonCard} />
						))}
					</div>
				) : posts.length === 0 ? (
					<div className={styles.emptyFeedMessage}>
						<p>No explore posts found.</p>
					</div>
				) : (
					<div className={styles.ExploreImageContainer}>
						{posts.map((post, index) => {
							// Staggered masonry classes every 7 items
							const isRow2 = index % 7 === 0;
							const isRow21 = index % 7 === 4;
							const extraClass = isRow2
								? styles.row2
								: isRow21
								? styles.row21
								: "";

							return (
								<ExplorePostCard
									key={post._id || index}
									post={post}
									variant="grid"
									className={extraClass}
								/>
							);
						})}
					</div>
				)}
			</div>
		</>
	);
};

export default ExploreYourWorld;
